import type { ClipScan } from '../core/scan';
import { scanCacheKey, type ScanMessage, type ScanReply } from '../worker/protocol';
import type { ScanRequest } from './queue';

const MAX_WORKERS = 4;

interface Call {
  readonly worker: Worker;
  readonly resolve: (scan: ClipScan) => void;
  readonly reject: (error: Error) => void;
}

export const scanWorkerCount = (): number => Math.max(1, Math.min(MAX_WORKERS, (navigator.hardwareConcurrency || 2) - 1));

export class ScanPool {
  private readonly idle: Worker[];
  private readonly all: readonly Worker[];
  private readonly calls = new Map<number, Call>();
  private nextId = 0;

  constructor(url: string, size: number) {
    this.all = Array.from({ length: size }, () => this.spawn(url));
    this.idle = [...this.all];
  }

  readonly run = (request: ScanRequest): Promise<ClipScan> => {
    const worker = this.idle.pop();
    if (!worker) return Promise.reject(new Error('every scan worker is busy'));
    const id = this.nextId++;
    const { ref } = request.clip;
    const name = ref.path.slice(ref.path.lastIndexOf('/') + 1);
    const message: ScanMessage = { id, file: ref.file, cacheKey: scanCacheKey(name, ref.size, ref.lastModified), telemetry: request.telemetry };
    return new Promise((resolve, reject) => {
      this.calls.set(id, { worker, resolve, reject });
      worker.postMessage(message);
    });
  };

  terminate(): void {
    this.all.forEach((worker) => worker.terminate());
    this.calls.forEach((call) => call.reject(new Error('scan cancelled')));
    this.calls.clear();
  }

  private spawn(url: string): Worker {
    const worker = new Worker(url, { type: 'module' });
    worker.addEventListener('message', (event: MessageEvent<ScanReply>) => this.settle(event.data));
    worker.addEventListener('error', (event) => this.crash(worker, event.message || 'the scan worker failed to start'));
    return worker;
  }

  private settle(reply: ScanReply): void {
    const call = this.calls.get(reply.id);
    if (!call) return;
    this.calls.delete(reply.id);
    this.idle.push(call.worker);
    if (reply.ok) call.resolve(reply.scan);
    else call.reject(new Error(reply.error));
  }

  private crash(worker: Worker, message: string): void {
    for (const [id, call] of this.calls) {
      if (call.worker !== worker) continue;
      this.calls.delete(id);
      this.idle.push(worker);
      call.reject(new Error(message));
    }
  }
}
